<script setup lang="ts">
import {computed} from "vue";
import {CircleCheck, PackageOpen} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import dateHelper from "@/core/helpers/dateHelper";
import {VoucherCheckResult} from "@/birthday-gift/interfaces";

const props = defineProps<{
    result: VoucherCheckResult | null;
    busy: boolean;
    /** Transient failure of the handover, the code stays valid. */
    error?: string | null;
}>();

const emit = defineEmits<{
    (e: 'cancel'): void;
    (e: 'redeem'): void;
}>();

const patient = computed(() => props.result?.patient);

const initials = computed(() => {
    const first = patient.value?.first_name?.charAt(0) ?? '';
    const last = patient.value?.last_name?.charAt(0) ?? '';

    return `${first}${last}`.toUpperCase();
});

const birthday = computed(() => patient.value?.birth_date
    ? dateHelper.shortFormat(patient.value.birth_date, 'D MMMM')
    : null);
</script>

<template>
  <div class="overflow-hidden rounded-xl border-2 border-solid border-secondary">
    <div class="flex items-center gap-2.5 bg-secondary px-4 py-2.5 text-sm font-semibold text-primary-dark">
      <CircleCheck class="h-5 w-5" />Cadeau valide · {{ result?.code }}
    </div>

    <div class="flex flex-col gap-4 p-4">
      <div class="flex items-center gap-3">
        <span class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-lightPurple-lightest font-semibold text-primary">
          {{ initials }}
        </span>
        <div class="min-w-0">
          <div class="text-lg font-bold">
            {{ patient?.first_name }} {{ patient?.last_name }}
          </div>
          <div class="text-xs text-muted-foreground">
            <template v-if="birthday">Anniversaire le {{ birthday }} · </template>{{ patient?.age }} ans
          </div>
        </div>
      </div>

      <div class="rounded-xl bg-primary-lightest p-4">
        <div class="mb-1.5 text-xs font-semibold text-primary">
          Cadeau à remettre
        </div>
        <div class="mb-2.5 text-base font-bold leading-snug">
          {{ result?.gift?.name }}
        </div>
        <div
          v-if="result?.gift?.counter_note"
          class="border-t border-primary/10 pt-2.5 text-sm leading-relaxed text-gray-dark"
        >
          {{ result.gift.counter_note }}
        </div>
      </div>

      <p
        v-if="error"
        class="m-0 text-sm font-medium text-red-600"
      >
        {{ error }}
      </p>

      <div class="flex flex-wrap justify-end gap-2.5">
        <Button
          variant="outline"
          :disabled="busy"
          @click="emit('cancel')"
        >
          Annuler
        </Button>
        <Button
          :disabled="busy"
          @click="emit('redeem')"
        >
          <PackageOpen class="h-4 w-4" />{{ busy ? 'Enregistrement…' : 'Cadeau remis' }}
        </Button>
      </div>
    </div>
  </div>
</template>
